import type { ComponentType } from 'react';
import type { Permission } from '@wr/shared';
import type { IconName } from './components/Icon.js';
import type { NavKey } from './nav.js';
import { HubSpotStatus } from './screens/admin/HubSpot.js';
import { KioskPin } from './screens/admin/KioskPin.js';
import { Purges } from './screens/admin/Purges.js';
import { StagingReset } from './screens/admin/StagingReset.js';
import { ContentScreen } from './screens/content/ContentScreen.js';
import { EventsScreen } from './screens/events/EventsScreen.js';
import { KioskScreen } from './screens/kiosk/KioskScreen.js';
import { PackagesScreen } from './screens/packages/PackagesScreen.js';
import { RecapScreen, WrapUpCurrent } from './screens/recap/RecapScreen.js';
import { ExpensesScreen, ReceiptScreen } from './screens/expenses/ExpensesScreen.js';
import { AddLead } from './screens/leads/Pickers.js';
import { TodoScreen } from './screens/leads/TodoScreen.js';
import { PeopleScreen } from './screens/people/PeopleScreen.js';
import { Scan } from './screens/capture/Scan.js';
import { DeletedProjects } from './screens/roi/DeletedProjects.js';
import { RoiScreen } from './screens/roi/RoiScreen.js';
import { TravelScreen } from './screens/travel/TravelScreen.js';

/**
 * The screens of the modules, plugged into the navigation of nav.ts.
 *
 * An entry of the navigation that has no screen here opens its "Not built
 * yet" page. A module built in its own session adds its line here, at the
 * end, and nothing else in the shared files (CLAUDE.md, Parallel work). The
 * screen is mounted at `${path}/*` and owns its sub-pages with its own
 * <Routes> (relative paths: "new", ":id"); the entry's permission is
 * checked before it opens, and the server checks again on every call.
 */
export const SCREENS: Partial<Record<NavKey, ComponentType>> = {
  scan: Scan,
  roi: RoiScreen,
  people: PeopleScreen,
  addLead: AddLead,
  todo: TodoScreen,
  expenses: ExpensesScreen,
  receipt: ReceiptScreen,
  events: EventsScreen,
  recap: RecapScreen,
  wrapup: WrapUpCurrent,
  content: ContentScreen,
  packages: PackagesScreen,
  kiosk: KioskScreen,
  travel: TravelScreen,
};

/**
 * The tools behind Settings (admins only; never configuration). A later
 * admin tool (kiosk PIN, ROI restore, privacy erasure...) adds its line at
 * the end, with its screen, mounted at `${path}/*`.
 */
export interface SettingsTool {
  path: string;
  icon: IconName;
  title: string;
  text: string;
  allowed(can: (p: Permission) => boolean): boolean;
  /** Absent for the first tools, whose routes App.tsx declares itself. */
  screen?: ComponentType;
}

export const SETTINGS_TOOLS: SettingsTool[] = [
  {
    path: '/admin/users',
    icon: 'contacts',
    title: 'Users',
    text: 'Invite people, give roles, reset an authenticator app',
    allowed: (can) => can('admin.users.read'),
  },
  {
    path: '/admin/audit',
    icon: 'edit',
    title: 'Audit log',
    text: 'Who did what, and when',
    allowed: (can) => can('admin.audit.read'),
  },
  {
    path: '/admin/roi',
    icon: 'roi',
    title: 'Deleted ROI projects',
    text: 'Restore a deleted ROI project',
    allowed: (can) => can('roi.restore'),
    screen: DeletedProjects,
  },
  {
    path: '/admin/kiosk-pin',
    icon: 'lock',
    title: 'Kiosk PIN',
    text: 'The PIN to leave Hand the tablet and the booth kiosk',
    allowed: (can) => can('admin.kiosk.pin'),
    screen: KioskPin,
  },
  {
    path: '/admin/hubspot',
    icon: 'sync',
    title: 'HubSpot',
    text: 'What was sent, what waits, what failed and why',
    allowed: (can) => can('admin.hubspot.manage'),
    screen: HubSpotStatus,
  },
  {
    path: '/admin/staging-reset',
    icon: 'sync',
    title: 'Reset staging data',
    text: 'Empty the staging portal and what it created in the test HubSpot',
    // Granted on staging only: the server leaves it out everywhere else.
    allowed: (can) => can('admin.staging.reset'),
    screen: StagingReset,
  },
  {
    path: '/admin/purges',
    icon: 'clock',
    title: 'Retention purges',
    text: 'What the nightly purges deleted for good, rule by rule',
    allowed: (can) => can('admin.audit.read'),
    screen: Purges,
  },
];
